import { TextField } from "@king-ict/eupisi-ui/components";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { FormCard } from "./FormCard";
import { FetchedDiffValue } from "../../features/Request/EnrollmentForm/components/FetchedDiffValue";
import { Address } from "../../models/Shared/Address";

interface AddressDisplayProps {
  address?: Address | null;
  fetchedAddress?: Address | null;
  canApply?: boolean;
  title?: string;
}

const EMPTY_ADDRESS: Address = {
  county: "",
  countyId: undefined,
  municipality: "",
  municipalityId: undefined,
  town: "",
  townId: undefined,
  street: "",
  streetId: undefined,
  houseNumber: "",
  houseNumberId: undefined,
  postalCode: "",
};

/**
 * Component for displaying address details in read-only mode.
 * Shows the current address values and highlights differences between current and fetched address data.
 *
 * @param {Object} props - Component props
 * @param {Address} [props.address] - Current address object containing address details
 * @param {Address} [props.fetchedAddress] - Fetched address object to compare against current address
 * @param {boolean} [props.canApply=false] - Whether to show differences between addresses
 * @param {string} [props.title] - Optional title for the address section
 *
 * @example
 * <AddressDisplay
 *   address={currentAddress}
 *   fetchedAddress={newAddress}
 *   canApply={true}
 *   title="Permanent Address"
 * />
 */

export const AddressDisplay = ({
  address,
  fetchedAddress,
  canApply = false,
  title,
}: AddressDisplayProps) => {
  const { t } = useTranslation();
  const a: Address = address ?? EMPTY_ADDRESS;
  const f: Address = fetchedAddress ?? EMPTY_ADDRESS;

  return (
    <FormCard cardTitle={title}>
      <Row>
        <Col md="4">
          <TextField
            label={t("common.schoolCandidateModel.countyList")}
            value={a.county || ""}
            readonly
            plaintext
          />
          <FetchedDiffValue
            show={canApply && !!fetchedAddress && f.county !== a.county}
          >
            {f.county}
          </FetchedDiffValue>
        </Col>
        <Col md="4">
          <TextField
            label={t("common.schoolCandidateModel.municipalityList")}
            value={a.municipality || ""}
            readonly
            plaintext
          />
          <FetchedDiffValue
            show={
              canApply && !!fetchedAddress && f.municipality !== a.municipality
            }
          >
            {f.municipality}
          </FetchedDiffValue>
        </Col>
        <Col md="4">
          <TextField
            label={t("common.schoolCandidateModel.townList")}
            value={a.town || ""}
            readonly
            plaintext
          />
          <FetchedDiffValue
            show={canApply && !!fetchedAddress && f.town !== a.town}
          >
            {f.town}
          </FetchedDiffValue>
        </Col>
      </Row>
      <Row>
        <Col md="4">
          <TextField
            label={t("common.schoolCandidateModel.streetList")}
            value={a.street || ""}
            readonly
            plaintext
          />
          <FetchedDiffValue
            show={canApply && !!fetchedAddress && f.street !== a.street}
          >
            {f.street}
          </FetchedDiffValue>
        </Col>
        <Col md="4">
          <TextField
            label={t("common.schoolCandidateModel.houseNumberSelector")}
            value={a.houseNumber || ""}
            readonly
            plaintext
          />
          <FetchedDiffValue
            show={
              canApply && !!fetchedAddress && f.houseNumber !== a.houseNumber
            }
          >
            {f.houseNumber}
          </FetchedDiffValue>
        </Col>
        <Col md="4">
          <TextField
            label={t("common.schoolCandidateModel.postalCode")}
            value={a.postalCode || ""}
            readonly
            plaintext
          />
          <FetchedDiffValue
            show={canApply && !!fetchedAddress && f.postalCode !== a.postalCode}
          >
            {f.postalCode}
          </FetchedDiffValue>
        </Col>
      </Row>
    </FormCard>
  );
};
